import React from "react";
import { Box, Button, Modal } from "@mui/material";

export interface AlertDialogProps {
    open: boolean;
    appPrefix?: string;
    title: string;
    message: string;
    severity?: "info" | "warning" | "error" | "success";
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
    onCancel?: () => void;
}
const SEVERITY_STYLES = {
    info: {
        icon: "ℹ️",
        headerBg: "bg-blue-50",
        titleColor: "text-blue-950",
        border: "border-blue-300",
        iconBg: "bg-blue-100",
        iconColor: "text-blue-700",
    },
    warning: {
        icon: "⚠️",
        headerBg: "bg-amber-50",
        titleColor: "text-amber-950",
        border: "border-amber-300",
        iconBg: "bg-amber-100",
        iconColor: "text-amber-700",
    },
    error: {
        icon: "✕",
        headerBg: "bg-red-50",
        titleColor: "text-red-950",
        border: "border-red-300",
        iconBg: "bg-red-100",
        iconColor: "text-red-700",
    },
    success: {
        icon: "✓",
        headerBg: "bg-green-50",
        titleColor: "text-green-950",
        border: "border-green-300",
        iconBg: "bg-green-100",
        iconColor: "text-green-700",
    },
} as const;

export const AlertDialog: React.FC<AlertDialogProps> = ({
    open,
    appPrefix = "CBA",
    title,
    message,
    severity = "info",
    confirmLabel = "OK",
    cancelLabel,
    onConfirm,
    onCancel,
}) => {
    const s = SEVERITY_STYLES[severity];

    return (

        <Modal
            open={open}
            onClose={onCancel ?? onConfirm}
            onKeyDown={(e) => {
                if (e.key === "Escape") (onCancel ?? onConfirm)();
                if (e.key === "Enter") onConfirm();
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "95%",
                    maxWidth: "480px",
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    borderRadius: "12px",
                    outline: "none",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <div
                    className={`${s.headerBg} ${s.border} border-b px-5 py-4 flex items-center gap-3`}
                >
                    <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${s.iconBg} ${s.iconColor}`}
                    >
                        <span className="text-base font-semibold leading-none">{s.icon}</span>
                    </div>

                    <h3 className={`text-sm font-semibold ${s.titleColor}`}>
                        {appPrefix ?? "CBA"} — {title}
                    </h3>
                </div>

                {/* Body */}
                <div className="px-5 py-4">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {message}
                    </p>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
                    {cancelLabel && onCancel && (
                        <Button variant="outlined" size="small" onClick={onCancel}>
                            {cancelLabel}
                        </Button>
                    )}

                    <Button variant="contained" size="small" onClick={onConfirm}
                        color={severity === "error" ? "error" : "primary"}
                        autoFocus>
                        {confirmLabel}
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};